import {
    ErrorResponse,
    ErrorTimeout,
    User,
    UserManager,
    WebStorageStateStore,
} from 'oidc-client-ts';
import {
    OIDC_SCOPES,
    getBrokerAuthConfig,
    loginRedirectFailed,
    signOutFailed,
    signOutStarted,
} from './auth';

/**
 * Authorization-code login and token acquisition against the identity broker.
 *
 * Renewal runs on a refresh token (`offline_access`) rather than a hidden
 * iframe: a broker that sends `frame-ancestors 'none'` blocks framed flows
 * outright, so iframe-based silent renewal and session monitoring can never
 * complete against it.
 */

/**
 * Web Lock that serialises refresh-token grants across every tab sharing the
 * stored user. Two tabs redeeming the same refresh token race, and where the
 * broker rotates refresh tokens the loser's grant is refused as a reuse.
 */
const RENEWAL_LOCK = 'workspace-broker-token-renewal';

/**
 * How long a renewal waits for the broker to answer. Without a bound, a broker
 * that accepts the request and never answers would hold the renewal lock, and
 * with it every token read in every tab, indefinitely.
 */
const RENEWAL_TIMEOUT_SECONDS = 10;

/**
 * Pauses before retrying a renewal that failed without an answer from the
 * broker — a network drop or an unavailable token endpoint — which may well
 * pass. One entry per retry.
 */
const RENEWAL_RETRY_DELAYS_MS = [2_000, 5_000];

/**
 * The broker gave no answer to a session renewal, even after retrying: the
 * network dropped, or the token endpoint was unavailable. Says nothing about
 * whether the session is still valid, unlike a refusal.
 */
export class SessionRenewalUnansweredError extends Error {
    public constructor() {
        super('The identity broker did not answer the session renewal.');
        this.name = 'SessionRenewalUnansweredError';
    }
}

let userManager: UserManager | null = null;

/** The renewal in flight in this tab, which later callers join. */
let renewal: Promise<User | null> | null = null;

/**
 * The refresh token the broker last refused. Its answer for that token will not
 * change, and the expiry timers fire again for as long as the token stays
 * stored, so it is not asked again.
 */
let refusedRefreshToken: string | null = null;

/**
 * The broker's UserManager, created on first use.
 *
 * @throws {AuthConfigurationError} when the broker configuration is missing
 *     or malformed.
 */
export const getUserManager = (): UserManager => {
    if (userManager === null) {
        const { authority, clientId } = getBrokerAuthConfig();
        userManager = new UserManager({
            authority,
            client_id: clientId,
            redirect_uri: window.location.origin,
            // Matches the Entra path, which returns to the /logout route so
            // the user gets a confirmation page rather than a bare reload.
            post_logout_redirect_uri: `${window.location.origin}/logout`,
            response_type: 'code',
            // Query mode, because a fragment response arrives with `state`
            // double-encoded (zitadel/zitadel#12645) and the callback then
            // rejects it as a mismatch.
            response_mode: 'query',
            scope: OIDC_SCOPES.join(' '),
            // The user's name and email are read from the userinfo endpoint,
            // since the broker leaves them out of the ID token unless the
            // client is configured to include them.
            loadUserInfo: true,
            // Renewal is driven through renewSession instead, so that it is
            // serialised with the renewals token reads start (see there).
            automaticSilentRenew: false,
            monitorSession: false,
            userStore: new WebStorageStateStore({ store: window.localStorage }),
        });
    }
    return userManager;
};

/**
 * Removes login state left behind by attempts that never reached the callback.
 * Each abandoned redirect stores a state entry that nothing else collects, so
 * without this they accumulate in storage for the lifetime of the browser
 * profile.
 */
export const clearStaleLoginState = async (): Promise<void> =>
    getUserManager().clearStaleState();

/** Discards the cached UserManager. For tests, which rebuild configuration. */
export const resetUserManager = (): void => {
    userManager = null;
    renewal = null;
    refusedRefreshToken = null;
};

/**
 * True when the current URL carries the broker's answer to a login redirect —
 * an authorization code, or an error in its place — which must be handled
 * before anything reads the session.
 */
export const isLoginRedirectCallback = (): boolean => {
    const params = new URLSearchParams(window.location.search);
    return params.has('state') && (params.has('code') || params.has('error'));
};

/**
 * Redeems the authorization response in the current URL and returns the
 * signed-in user.
 *
 * A sign-in that did not complete — the broker answered with an error, or the
 * code could not be redeemed — returns null after recording the failure, so the
 * login page offers a retry rather than redirecting straight back into a
 * refusal that may be the same every time.
 *
 * The response is stripped from the address bar either way: its state entry is
 * spent once read, so leaving it would fail every reload.
 *
 * @throws {AuthConfigurationError} when the broker configuration is missing
 *     or malformed.
 */
export const completeLoginRedirect = async (): Promise<User | null> => {
    const manager = getUserManager();
    try {
        return await manager.signinRedirectCallback();
    } catch (error) {
        console.error('Sign-in did not complete:', error);
        loginRedirectFailed();
        return null;
    } finally {
        window.history.replaceState(
            {},
            document.title,
            window.location.pathname
        );
    }
};

const withRenewalLock = <T>(task: () => Promise<T>): Promise<T> =>
    'locks' in navigator ? navigator.locks.request(RENEWAL_LOCK, task) : task();

const renewWithRetry = async (): Promise<User | null> => {
    const manager = getUserManager();
    const { accessTokenExpiringNotificationTimeInSeconds } = manager.settings;
    for (let attempt = 0; ; attempt++) {
        const stored = await manager.getUser();
        if (stored === null) {
            return null;
        }
        // Read under the lock: a token with longer to live than the renewal
        // window was renewed by another tab while this one waited, and
        // redeeming the refresh token that tab has already spent would fail.
        if (
            (stored.expires_in ?? 0) >
            accessTokenExpiringNotificationTimeInSeconds
        ) {
            // Re-arms this tab's expiry timers and publishes the session.
            await manager.events.load(stored);
            return stored;
        }
        if (
            stored.refresh_token === undefined ||
            stored.refresh_token === refusedRefreshToken
        ) {
            return null;
        }
        try {
            // Passed per call: the refresh-token grant does not fall back to
            // the configured timeout.
            return await manager.signinSilent({
                silentRequestTimeoutInSeconds: RENEWAL_TIMEOUT_SECONDS,
            });
        } catch (error) {
            // An error response is the broker's answer — the grant is expired,
            // revoked or already used — and asking again gets the same one.
            if (error instanceof ErrorResponse) {
                refusedRefreshToken = stored.refresh_token;
                console.warn('Could not renew the broker session:', error);
                return null;
            }
            // Only a request the broker did not answer is worth asking
            // again: a failed fetch, or the renewal's own timeout. Any
            // other failure is an answer that will not change — a proxy's
            // HTML 401, a body that is not JSON — and the session is over.
            if (
                !(error instanceof TypeError || error instanceof ErrorTimeout)
            ) {
                console.warn('Could not renew the broker session:', error);
                return null;
            }
            if (attempt >= RENEWAL_RETRY_DELAYS_MS.length) {
                console.warn('Could not renew the broker session:', error);
                throw new SessionRenewalUnansweredError();
            }
            await new Promise((resolve) =>
                setTimeout(resolve, RENEWAL_RETRY_DELAYS_MS[attempt])
            );
        }
    }
};

/**
 * Renews the session from its refresh token and returns the renewed user, or
 * null when there is no session to renew or the broker refused to renew it.
 *
 * Every renewal goes through here — ahead of expiry, at expiry, and on reading
 * an expired token — so that concurrent callers in a tab share one grant, and
 * tabs take turns, each adopting a token another has already renewed. A failure
 * the broker did not answer is retried before it is reported.
 *
 * @throws {SessionRenewalUnansweredError} when the broker never answered.
 */
export const renewSession = (): Promise<User | null> => {
    renewal ??= withRenewalLock(renewWithRetry).finally(() => {
        renewal = null;
    });
    return renewal;
};

/**
 * The stored user, or null when there is no session. A user whose access token
 * has expired is renewed from its refresh token; a renewal the broker refused is
 * reported as no session, so the caller falls back to logging in.
 *
 * @throws {SessionRenewalUnansweredError} when the broker never answered the
 *     renewal, which leaves the session's state unknown.
 */
export const getStoredUser = async (): Promise<User | null> => {
    const user = await getUserManager().getUser();
    if (user === null || !user.expired) {
        return user;
    }
    return renewSession();
};

/** Starts a redirect login. Resolves only as the browser navigates away. */
export const startLoginRedirect = async (): Promise<void> =>
    getUserManager().signinRedirect();

/**
 * Starts a redirect login in which the broker shows its account chooser even
 * when it holds a session, so that a user signed in as an account that has no
 * access can sign in as another. Resolves only as the browser navigates away.
 *
 * The stored user is replaced when the new sign-in completes; it is not removed
 * first, since that would read as signed out and start an ordinary sign-in
 * alongside this one.
 */
export const startAccountSwitchRedirect = async (): Promise<void> =>
    getUserManager().signinRedirect({ prompt: 'select_account' });

/**
 * Ends the broker session and returns to the application origin. The id token
 * is passed as a hint so the broker can end its own session without prompting
 * the user to pick which one to sign out of.
 */
export const startLogoutRedirect = async (): Promise<void> => {
    const manager = getUserManager();
    const user = await manager.getUser();
    signOutStarted();
    try {
        await manager.signoutRedirect({ id_token_hint: user?.id_token });
    } catch (error) {
        signOutFailed();
        throw error;
    }
};
